import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";

import { SubscriptionVariants } from "../../../types/rtk/subscription/subscription";
import LogoSvg from "../../atoms/LogoSvg/LogoSvg";
import ActiveButton from "../../../assets/images/RADIO-BUTTON-ACTIVE.svg";
import InactiveButton from "../../../assets/images/RADIO-BUTTON-INACTIVE.svg";
import Chip from "../../atoms/Chip/Chip";
import Text from "../../atoms/Text/Text";
import { getSubscriptionTitle } from "../../../helpers/getSubscriptionTitle";
import { calculateSavingsBetweenPeriods } from "../../../helpers/savingsCalculator";
import { RootState } from "../../../types/rtk/api-config/types";
import { useAppDispatch, useAppSelector } from "../../../rtk/_config/hooks";
import { setBasket } from "../../../rtk/basket/basket";
import { formatPrice } from "../../../helpers/currencyHelper";
import { useAbTestVariant } from "../../../hooks/useAbTestVariant";

interface Props {
  data?: SubscriptionVariants;
  index?: number;
  isActive: boolean;
  onSelect?: () => void;
  offers?: SubscriptionVariants[];
}

const SubscriptionCard = ({ data, index, isActive, onSelect, offers }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const basket = useAppSelector((state: RootState) => state.basket);

  const giftcard_reduction = Math.round((basket.giftcard_ratio || 0) * 100);

  const abVariant = useAbTestVariant();

  // Retrieve the selected offer data based on basket.offer_id
  const selectedOffer = offers?.find(offer => offer.id === basket.offer_id);

  const handleSetActive = () => {
    onSelect ? onSelect() : (
      data
        ? dispatch(setBasket({ ...basket, offer_id: data.id, giftcard: null, giftcard_ratio: null, promo: null, promocode_ratio: null }))
        : dispatch(setBasket({ ...basket, offer_id: null }))
    );
  };

  return (
    <Box>
      <Box
        className={`SubscriptionCard ${isActive && "isSubscriptionCardActive"}`}
        onClick={handleSetActive}
      >
        {/* Show chip logic (orange/grey label at the top right of the card) */}
        {
          data?.period === 36 ? (
            <Chip
              text={`${t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.CHIP_ECO_OFFER")}`}
              isActive={isActive}
            />
          ) : data?.period === 24 ? (
            <Chip
              text={`${t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.CHIP_POPULAR_OFFER")}`}
              isActive={isActive}
            />
          ) : null
        }
        <Box className="subscriptionRadioButton">
          <LogoSvg src={isActive ? ActiveButton : InactiveButton} />
        </Box>
        <Box className="subscriptionTitle">
          <Text className="bold">
            {data ? (
              getSubscriptionTitle(data.period)
            ) : (
              basket.giftcard
                ? `${t("subscription:FIRST_PAGE.MODAL_GIFT_CARD.INPUT_LABEL")} : ${basket.giftcard}`
                : t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.SUBSCRIPTION_TITLE_GIFT_CARD")
            )}
          </Text>
          <Box className="priceChipRow">
            <Text className="price bold strikethrough">
              {(data && data.period === 36) ? (
                formatPrice(data.price.amount + 20, data.price.currency)
              ) : null}
            </Text>
            {(data && data.period === 36) ? (
              <Chip className="green" text={t("subscriptions:OFFERS.SAVINGS", { price: formatPrice(20, data.price.currency) })} />
            ) : null}
          </Box>
          <Text className="price">
            {(data && data.period !== 1) ? (
              t("subscription:PREPAYED", { price: formatPrice(data.price.amount, data.price.currency) })
            ) : null}
          </Text>
          {(!data) && basket.giftcard && basket.offer_id && selectedOffer &&
            <Text className="price">
              {getSubscriptionTitle(selectedOffer.period)}
              {" "}
              {(giftcard_reduction < 100) ? t("subscription:FOR_MINUS_X_PERCENT", { percent: giftcard_reduction })
                : t("subscription:FREE")}
            </Text>
          }
        </Box>
        {/* Price box on the right side */}
        {data && (
          <Box className="subscriptionPrice">
            <Box className="prices">
              <Text className={data.period !== 1 ? "color bold" : "bold"}>
                {`${formatPrice(data.price.amount / data.period, data.price.currency)}`}
              </Text>
              <Text className="bold">
                {` / ${t("subscription:MONTH")}`}
              </Text>
            </Box>
          </Box>
        )}
      </Box>
      {/* Tip below the card */}
      {isActive && !basket.giftcard && (
        <Box className={`subscriptionTip ${data?.period === 1 ? 'subscriptionTip--red' : ''}`}>
          <Text children={
            data?.period !== 1
              ? t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.TIP_30_DAYS_WARRANTY")
              : abVariant === "A"
                ? t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.TIP_ONE_MONTH", { price: formatPrice(calculateSavingsBetweenPeriods(offers || [], 12, 1) || 0, data?.price.currency || "") })
                : t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.TIP_ONE_MONTH_ALT", { loss: formatPrice(calculateSavingsBetweenPeriods(offers || [], 12, 1, "amount") || 0, data?.price.currency || "") })
          } />
        </Box>
      )}
    </Box>
  );
};

export default SubscriptionCard;